/**
 * Formulation, Scoring, and Optimization API Service
 *
 * This service handles AI-powered formulations, score calculations, and recipe optimizations.
 * It follows the same patterns as other services in the project, extending the Api base class
 * for automatic authentication and error handling.
 */

import { Api } from "./api";
import config from "../utils/config";

// ==================== Formulation Interfaces ====================

export interface CreateFormulationRequest {
	description: string;
	product_type: string;
	serving_size: number;
	target_nutrition: {
		calories?: number;
		protein?: number;
		carbs?: number;
		fat?: number;
		fiber?: number;
	};
	constraints?: {
		max_cost?: number;
		allergens?: string[];
		dietary?: string[];
	};
	model?: "claude" | "gemini" | "journey_ai";
}

export interface GenerateFormulationRequest {
	description: string;
	max_cost?: number;
	model?: "claude" | "gemini" | "journey_ai";
}

export interface FormulationResponse {
	success: boolean;
	data?: any;
	message?: string;
	error?: string;
}

// ==================== Optimization Interfaces ====================

export type OptimizationObjective =
	| "lower_sugar"
	| "improve_sustainability"
	| "reduce_cost"
	| "boost_protein"
	| "clean_label";

export interface OptimizeRecipeRequest {
	objective: OptimizationObjective;
	max_cost_increase?: number;
	maintain_taste?: boolean;
	allergen_restrictions?: string[];
	dietary_requirements?: string[];
	model?: "claude" | "gemini" | "journey_ai";
}

export interface OptimizationResponse {
	success: boolean;
	data?: any;
	message?: string;
	error?: string;
}

export interface Formulation {
	id: string;
	name: string;
	[key: string]: any;
}

export interface GetAllFormulationsResponse {
	success: boolean;
	data?: Formulation[];
	message?: string;
	error?: string;
}

// ==================== Score Calculation Interfaces ====================

export interface ScoreCalculationResponse {
	success: boolean;
	data?: any;
	message?: string;
	error?: string;
}

/**
 * Formulation, Scoring, and Optimization Service
 * Extends the Api base class to leverage automatic authentication and error handling
 */
class FormulationService extends Api {
	/**
	 * Create a formulation with detailed parameters
	 *
	 * @param request - The formulation creation request
	 * @returns Promise resolving to a FormulationResponse
	 */
	createFormulation = async (request: CreateFormulationRequest): Promise<FormulationResponse> => {
		try {
			// Validate required fields
			if (!request.description || !request.description.trim()) {
				return {
					success: false,
					error: "Description is required"
				};
			}

			if (!request.product_type || !request.product_type.trim()) {
				return {
					success: false,
					error: "Product type is required"
				};
			}

			if (!request.serving_size || request.serving_size <= 0) {
				return {
					success: false,
					error: "Serving size must be greater than 0"
				};
			}

			// Prepare request body
			const requestBody: any = {
				description: request.description.trim(),
				product_type: request.product_type.trim(),
				serving_size: Number(request.serving_size)
			};

			// Add target nutrition if provided
			if (request.target_nutrition) {
				requestBody.target_nutrition = {};
				if (request.target_nutrition.calories !== undefined) {
					requestBody.target_nutrition.calories = Number(request.target_nutrition.calories);
				}
				if (request.target_nutrition.protein !== undefined) {
					requestBody.target_nutrition.protein = Number(request.target_nutrition.protein);
				}
				if (request.target_nutrition.carbs !== undefined) {
					requestBody.target_nutrition.carbs = Number(request.target_nutrition.carbs);
				}
				if (request.target_nutrition.fat !== undefined) {
					requestBody.target_nutrition.fat = Number(request.target_nutrition.fat);
				}
				if (request.target_nutrition.fiber !== undefined) {
					requestBody.target_nutrition.fiber = Number(request.target_nutrition.fiber);
				}
			}

			// Add constraints if provided
			if (request.constraints) {
				requestBody.constraints = {};
				if (request.constraints.max_cost !== undefined) {
					requestBody.constraints.max_cost = Number(request.constraints.max_cost);
				}
				if (request.constraints.allergens && request.constraints.allergens.length > 0) {
					requestBody.constraints.allergens = request.constraints.allergens;
				}
				if (request.constraints.dietary && request.constraints.dietary.length > 0) {
					requestBody.constraints.dietary = request.constraints.dietary;
				}
			}

			if (request.model) {
				requestBody.model = request.model;
			}

			// Make API call
			const response = await this.http.post(config.api.endpoints.formulations.create, requestBody);

			// Extract response data
			const responseData = response.data?.data || response.data;

			return {
				success: true,
				data: responseData,
				message: response.data?.message || "Formulation created successfully"
			};
		} catch (error) {
			console.error("Error in createFormulation:", error);

			const axiosError = error as any;
			if (axiosError.response) {
				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					"Failed to create formulation";

				return {
					success: false,
					error: errorMessage
				};
			}

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to create formulation. Please check your connection and try again."
			};
		}
	}

	/**
	 * Generate a formulation from a simple description
	 *
	 * @param request - The formulation generation request
	 * @returns Promise resolving to a FormulationResponse
	 */
	generateFormulation = async (request: GenerateFormulationRequest): Promise<FormulationResponse> => {
		try {
			// Validate input
			if (!request.description || !request.description.trim()) {
				return {
					success: false,
					error: "Description is required"
				};
			}

			// Prepare request body
			const requestBody: any = {
				description: request.description.trim()
			};

			if (request.max_cost !== undefined) {
				requestBody.max_cost = request.max_cost;
			}

			if (request.model) {
				requestBody.model = request.model;
			}

			// Make API call
			const response = await this.http.post(
				config.api.endpoints.formulations.generate,
				requestBody
			);

			// Extract response data
			const responseData = response.data?.data || response.data;

			return {
				success: true,
				data: responseData,
				message: response.data?.message || "Formulation generated successfully"
			};
		} catch (error) {
			console.error("Error in generateFormulation:", error);

			const axiosError = error as any;
			if (axiosError.response) {
				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					"Failed to generate formulation";

				return {
					success: false,
					error: errorMessage
				};
			}

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to generate formulation. Please check your connection and try again."
			};
		}
	}

	/**
	 * Calculate scores for a product
	 *
	 * @param productId - The ID of the product to calculate scores for
	 * @param model - Optional AI model to use
	 * @returns Promise resolving to a ScoreCalculationResponse
	 */
	calculateScore = async (productId: string, model?: "claude" | "gemini" | "journey_ai"): Promise<ScoreCalculationResponse> => {
		try {
			// Validate input
			if (!productId || !productId.trim()) {
				return {
					success: false,
					error: "Product ID is required"
				};
			}

			// Make API call — scores endpoint is GET but we pass model as query param if provided
			const url = model
				? `${config.api.endpoints.formulations.scores(productId)}?model=${model}`
				: config.api.endpoints.formulations.scores(productId);

			const response = await this.http.get(url);

			// Extract response data
			const responseData = response.data?.data || response.data;

			return {
				success: true,
				data: responseData,
				message: response.data?.message || "Scores calculated successfully"
			};
		} catch (error) {
			console.error("Error in calculateScore:", error);

			const axiosError = error as any;
			if (axiosError.response) {
				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					"Failed to calculate scores";

				return {
					success: false,
					error: errorMessage
				};
			}

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to calculate scores. Please check your connection and try again."
			};
		}
	}

	/**
	 * Get all formulations
	 *
	 * @returns Promise resolving to a GetAllFormulationsResponse
	 */
	getAllFormulations = async (): Promise<GetAllFormulationsResponse> => {
		try {
			// Make API call
			const response = await this.http.get(config.api.endpoints.formulations.all);

			// Extract response data - handle both array and object with data property
			const responseData = response.data?.data || response.data;

			// Ensure we have an array
			const formulations = Array.isArray(responseData) ? responseData : [];

			return {
				success: true,
				data: formulations,
				message: response.data?.message || "Formulations retrieved successfully"
			};
		} catch (error) {
			console.error("Error in getAllFormulations:", error);

			const axiosError = error as any;
			if (axiosError.response) {
				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					"Failed to retrieve formulations";

				return {
					success: false,
					error: errorMessage
				};
			}

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to retrieve formulations. Please check your connection and try again."
			};
		}
	}

	/**
	 * Optimize a recipe
	 *
	 * @param recipeId - The ID of the recipe/product to optimize
	 * @param request - The optimization request parameters
	 * @returns Promise resolving to an OptimizationResponse
	 */
	optimizeRecipe = async (
		recipeId: string,
		request: OptimizeRecipeRequest
	): Promise<OptimizationResponse> => {
		try {
			// Validate input
			if (!recipeId || !recipeId.trim()) {
				return {
					success: false,
					error: "Recipe ID is required"
				};
			}

			if (!request.objective) {
				return {
					success: false,
					error: "Optimization objective is required"
				};
			}

			// Prepare request body
			const requestBody: any = {
				objective: request.objective
			};

			if (request.max_cost_increase !== undefined) {
				requestBody.max_cost_increase = Number(request.max_cost_increase);
			}

			if (request.maintain_taste !== undefined) {
				requestBody.maintain_taste = Boolean(request.maintain_taste);
			}

			if (request.allergen_restrictions && request.allergen_restrictions.length > 0) {
				requestBody.allergen_restrictions = request.allergen_restrictions;
			}

			if (request.dietary_requirements && request.dietary_requirements.length > 0) {
				requestBody.dietary_requirements = request.dietary_requirements;
			}

			if (request.model) {
				requestBody.model = request.model;
			}

			// Make API call
			const response = await this.http.post(
				config.api.endpoints.formulations.optimize(recipeId),
				requestBody
			);

			// Extract response data
			const responseData = response.data?.data || response.data;

			return {
				success: true,
				data: responseData,
				message: response.data?.message || "Recipe optimized successfully"
			};
		} catch (error) {
			console.error("Error in optimizeRecipe:", error);

			const axiosError = error as any;
			if (axiosError.response) {
				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					"Failed to optimize recipe";

				return {
					success: false,
					error: errorMessage
				};
			}

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to optimize recipe. Please check your connection and try again."
			};
		}
	}
}

// Create a singleton instance of the service
const formulationService = new FormulationService();

/**
 * Create formulation API function
 */
export const createFormulationAPI = async (
	request: CreateFormulationRequest
): Promise<FormulationResponse> => {
	return formulationService.createFormulation(request);
};

/**
 * Generate formulation API function
 */
export const generateFormulationAPI = async (
	request: GenerateFormulationRequest
): Promise<FormulationResponse> => {
	return formulationService.generateFormulation(request);
};

/**
 * Calculate score API function
 */
export const calculateScoreAPI = async (productId: string, model?: "claude" | "gemini" | "journey_ai"): Promise<ScoreCalculationResponse> => {
	return formulationService.calculateScore(productId, model);
};

/**
 * Get all formulations API function
 */
export const getAllFormulationsAPI = async (): Promise<GetAllFormulationsResponse> => {
	return formulationService.getAllFormulations();
};

/**
 * Optimize recipe API function
 */
export const optimizeRecipeAPI = async (
	recipeId: string,
	request: OptimizeRecipeRequest
): Promise<OptimizationResponse> => {
	return formulationService.optimizeRecipe(recipeId, request);
};
